import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Share2, Link2, Check, Instagram, Send } from 'lucide-react';
import { toast } from 'sonner';

// TikTok inline SVG (lucide doesn't include it)
const TikTokIcon = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.93a8.23 8.23 0 0 0 4.78 1.52V7a4.83 4.83 0 0 1-1.85-.31z" />
  </svg>
);

const WhatsAppIcon = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.057 21.45a9.45 9.45 0 0 1-4.81-1.314l-.345-.205-3.578.94.954-3.487-.224-.357a9.426 9.426 0 0 1-1.464-5.045c0-5.211 4.255-9.45 9.467-9.45 2.526 0 4.9.984 6.687 2.77a9.394 9.394 0 0 1 2.77 6.687c-.002 5.21-4.247 9.461-9.457 9.461z" />
  </svg>
);

export default function ShareDialog({ url, title = '', description = '', triggerClassName = '', triggerLabel, triggerSize = 'sm', triggerVariant = 'outline', testIdPrefix = 'share' }) {
  const { language } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const tt = (en, es) => (language === 'es' ? es : en);
  const fullUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareText = title ? `${title} — ${fullUrl}` : fullUrl;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success(tt('Link copied to clipboard', 'Enlace copiado al portapapeles'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(tt('Could not copy link', 'No se pudo copiar el enlace'));
    }
  };

  const openWhatsApp = () => {
    const msg = encodeURIComponent(shareText);
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener,noreferrer');
  };

  const shareInstagram = async () => {
    // Instagram has no public share URL. Try Web Share API for mobile (system sheet)
    if (navigator.share) {
      try {
        await navigator.share({ title, text: description || title, url: fullUrl });
        return;
      } catch {
        // user dismissed or share failed; fall through
      }
    }
    // Desktop fallback: copy link and open Instagram
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success(tt('Link copied! Paste it on Instagram', '¡Enlace copiado! Pégalo en Instagram'));
    } catch {
      toast.error(tt('Could not copy link', 'No se pudo copiar el enlace'));
    }
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
  };

  const shareTikTok = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: description || title, url: fullUrl });
        return;
      } catch {
        // fall through
      }
    }
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success(tt('Link copied! Paste it on TikTok', '¡Enlace copiado! Pégalo en TikTok'));
    } catch {
      toast.error(tt('Could not copy link', 'No se pudo copiar el enlace'));
    }
    window.open('https://www.tiktok.com/', '_blank', 'noopener,noreferrer');
  };

  const useNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: description || title, url: fullUrl });
        setOpen(false);
      } catch {
        // user dismissed
      }
    }
  };

  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={triggerVariant}
          size={triggerSize}
          className={triggerClassName}
          data-testid={`${testIdPrefix}-trigger-btn`}
          aria-label={tt('Share property', 'Compartir propiedad')}
        >
          <Share2 className="w-4 h-4" />
          {triggerLabel && <span className="ml-1.5">{triggerLabel}</span>}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-primary" />
            {tt('Share this property', 'Compartir esta propiedad')}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {tt('Send this listing to friends or post it on social media.', 'Envía esta propiedad a tus amigos o publícala en redes sociales.')}
          </DialogDescription>
        </DialogHeader>

        {title && <p className="text-sm text-slate-600 line-clamp-2" data-testid="share-title">{title}</p>}

        {/* Link copy bar */}
        <div className="flex items-center gap-2">
          <Input value={fullUrl} readOnly className="text-xs bg-slate-50 h-10" data-testid="share-url-input" onClick={(e) => e.target.select()} />
          <Button onClick={copyLink} className="h-10 gap-1.5 bg-[#0B192C] hover:bg-[#1B2A4E] text-white" data-testid="share-copy-btn">
            {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
            {copied ? tt('Copied', 'Copiado') : tt('Copy', 'Copiar')}
          </Button>
        </div>

        {/* Channel buttons */}
        <div className="grid grid-cols-3 gap-2 mt-1">
          <button
            onClick={openWhatsApp}
            data-testid="share-whatsapp-btn"
            className="group flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-[#25D366] hover:bg-[#25D366]/5 transition-colors"
          >
            <span className="w-10 h-10 rounded-full bg-[#25D366] text-white flex items-center justify-center group-hover:scale-110 transition-transform">
              <WhatsAppIcon className="w-5 h-5" />
            </span>
            <span className="text-xs font-medium text-slate-700">WhatsApp</span>
          </button>

          <button
            onClick={shareInstagram}
            data-testid="share-instagram-btn"
            className="group flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-[#E1306C] hover:bg-[#E1306C]/5 transition-colors"
          >
            <span
              className="w-10 h-10 rounded-full text-white flex items-center justify-center group-hover:scale-110 transition-transform"
              style={{ background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
            >
              <Instagram className="w-5 h-5" />
            </span>
            <span className="text-xs font-medium text-slate-700">Instagram</span>
          </button>

          <button
            onClick={shareTikTok}
            data-testid="share-tiktok-btn"
            className="group flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-black hover:bg-slate-50 transition-colors"
          >
            <span className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center group-hover:scale-110 transition-transform">
              <TikTokIcon className="w-5 h-5" />
            </span>
            <span className="text-xs font-medium text-slate-700">TikTok</span>
          </button>
        </div>

        {hasNativeShare && (
          <Button
            variant="outline"
            onClick={useNativeShare}
            data-testid="share-native-btn"
            className="w-full gap-2 mt-1 border-[#D4AF37] text-[#0B192C] hover:bg-[#D4AF37]/10"
          >
            <Send className="w-4 h-4" />
            {tt('More sharing options', 'Más opciones para compartir')}
          </Button>
        )}

        <p className="text-[11px] text-slate-400 text-center pt-1">
          {tt(
            'Instagram & TikTok do not support direct web sharing — we copy the link so you can paste it.',
            'Instagram y TikTok no permiten compartir directo desde la web — copiamos el enlace para que lo pegues.'
          )}
        </p>
      </DialogContent>
    </Dialog>
  );
}
